import { useState, useEffect } from 'react';
import {
  collection, onSnapshot, doc, query, orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { AY_ADLARI, ayKey, paraCevir } from '../utils/takvim';
import { ChevronLeft, ChevronRight, Printer, CreditCard, CheckCircle2, AlertCircle } from 'lucide-react';

export default function AySonuRaporu() {
  const bugun = new Date();
  const [yil, setYil] = useState(bugun.getFullYear());
  const [ay, setAy] = useState(bugun.getMonth());
  const [ustalar, setUstalar] = useState([]);
  const [santiyeler, setSantiyeler] = useState([]);
  const [calisma, setCalisma] = useState({});
  const [avanslar, setAvanslar] = useState({});
  const [odemeler, setOdemeler] = useState([]);

  const ak = ayKey(yil, ay);

  useEffect(() => {
    const u = onSnapshot(query(collection(db, 'ustalar'), orderBy('olusturmaTarihi', 'desc')),
      snap => setUstalar(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const s = onSnapshot(collection(db, 'santiyeler'),
      snap => setSantiyeler(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u(); s(); };
  }, []);

  useEffect(() => {
    const u1 = onSnapshot(doc(db, 'aylikCalisma', ak), snap => {
      setCalisma(snap.exists() ? (snap.data().kayitlar || {}) : {});
    });
    const u2 = onSnapshot(doc(db, 'aylikAvans', ak), snap => {
      setAvanslar(snap.exists() ? snap.data() : {});
    });
    return () => { u1(); u2(); };
  }, [ak]);

  // Ödemeleri çek - ilgiliAy'e göre filtrele
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'odemeler'), orderBy('tarih', 'desc')),
      snap => {
        const tumOdemeler = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // ilgiliAy'e göre filtrele, yoksa tarih ayına göre
        const aylikOdemeler = tumOdemeler.filter(o => {
          if (o.ilgiliAy) return o.ilgiliAy === ak;
          const tarih = o.tarih?.toDate ? o.tarih.toDate() : new Date(o.tarih);
          return ayKey(tarih.getFullYear(), tarih.getMonth()) === ak;
        });
        setOdemeler(aylikOdemeler);
      }
    );
    return () => unsub();
  }, [ak]);

  function ayDegistir(delta) {
    let yeniAy = ay + delta;
    let yeniYil = yil;
    if (yeniAy > 11) { yeniAy = 0; yeniYil++; }
    if (yeniAy < 0) { yeniAy = 11; yeniYil--; }
    setAy(yeniAy);
    setYil(yeniYil);
  }

  function getSantiyeAd(sId) {
    return santiyeler.find(s => s.id === sId)?.ad || "Bilinmeyen Şantiye";
  }

  const raporSatirlari = ustalar.map(u => {
    const gunler = [];
    const lokasyonSet = new Set();

    Object.entries(calisma)
      .filter(([gk, ks]) => gk.startsWith(ak) && ks[u.id])
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([gk, ks]) => {
        const tarih = new Date(gk);
        const sAd = getSantiyeAd(ks[u.id]);
        gunler.push({ tarih, santiye: sAd });
        lokasyonSet.add(sAd);
      });

    // Avans hesapla
    let ustaAvans = 0;
    const avansVerisi = avanslar[u.id];
    if (avansVerisi) {
      if (typeof avansVerisi === 'number') {
        ustaAvans = avansVerisi;
      } else if (typeof avansVerisi === 'object') {
        ustaAvans = Object.values(avansVerisi).reduce((a, b) => a + Number(b || 0), 0);
      }
    }

    // Ödeme hesapla (ilgili ay için)
    let ustaOdeme = 0;
    const ustaOdemeler = odemeler.filter(o => o.ustaId === u.id);
    ustaOdemeler.forEach(o => {
      ustaOdeme += Number(o.tutar || 0);
    });

    const yovmiye = Number(u.yovmiye || u.yevmiye || 0);
    const brut = gunler.length * yovmiye;
    const net = brut - ustaAvans - ustaOdeme;

    // Ödeme durumu
    const odemeDurumu = brut > 0 
      ? (net <= 0 ? 'odenmis' : 'borclu')
      : (ustaAvans > 0 || ustaOdeme > 0 ? 'avans_odeme' : 'bos');

    return {
      ...u,
      gunler,
      lokasyonlar: [...lokasyonSet],
      yovmiye,
      avans: ustaAvans,
      odeme: ustaOdeme,
      odemeKayitlari: ustaOdemeler,
      brut,
      net,
      odemeDurumu
    };
  }).filter(u => u.gunler.length > 0 || u.avans > 0 || u.odeme > 0);

  const toplamBrut = raporSatirlari.reduce((t, u) => t + u.brut, 0);
  const toplamAvans = raporSatirlari.reduce((t, u) => t + u.avans, 0);
  const toplamOdeme = raporSatirlari.reduce((t, u) => t + u.odeme, 0);
  const toplamNet = raporSatirlari.reduce((t, u) => t + u.net, 0);

  const borcluSayisi = raporSatirlari.filter(u => u.odemeDurumu === 'borclu').length;
  const odenmisSayisi = raporSatirlari.filter(u => u.odemeDurumu === 'odenmis').length;

  return (
    <div className="px-4 md:px-6 py-6 md:py-8 max-w-4xl mx-auto space-y-6">

      {/* Kontroller: Mobilde dikey, tablette yatay */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-xl font-bold text-white">Ay Sonu Raporu</h1>
          <p className="text-xs text-stone-500 font-medium">Usta ve Şantiye Hakediş Özetleri</p>
        </div>
        <div className="flex items-center justify-between md:justify-end gap-3">
          <div className="flex items-center gap-2 bg-stone-900 border border-stone-800 rounded-xl px-3 py-2">
            <button onClick={() => ayDegistir(-1)} className="text-stone-500 hover:text-white p-1">
              <ChevronLeft size={18} />
            </button>
            <span className="text-xs md:text-sm font-semibold text-white min-w-[90px] md:min-w-[110px] text-center uppercase">
              {AY_ADLARI[ay]} {yil}
            </span>
            <button onClick={() => ayDegistir(1)} className="text-stone-500 hover:text-white p-1">
              <ChevronRight size={18} />
            </button>
          </div>
          <button onClick={() => window.print()}
            className="flex items-center gap-1.5 bg-white text-stone-900 font-bold px-4 py-2.5 rounded-xl text-xs md:text-sm hover:bg-stone-200 transition-colors shadow-lg">
            <Printer size={15} /> <span className="hidden sm:inline">Yazdır / PDF</span>
          </button>
        </div>
      </div>

      {/* Özet Durum Kartları */}
      {raporSatirlari.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print:hidden">
          <div className="bg-stone-900 border border-stone-800 rounded-2xl p-4">
            <div className="text-[9px] text-stone-500 font-black uppercase tracking-wider mb-1">Toplam Brüt</div>
            <div className="text-lg font-black text-white">{paraCevir(toplamBrut)}</div>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-2xl p-4">
            <div className="text-[9px] text-stone-500 font-black uppercase tracking-wider mb-1">Toplam Avans</div>
            <div className="text-lg font-black text-yellow-500">{paraCevir(toplamAvans)}</div>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-2xl p-4">
            <div className="text-[9px] text-stone-500 font-black uppercase tracking-wider mb-1">Yapılan Ödeme</div>
            <div className="text-lg font-black text-blue-400">{paraCevir(toplamOdeme)}</div>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-2xl p-4">
            <div className="text-[9px] text-stone-500 font-black uppercase tracking-wider mb-1">Net Ödenecek</div>
            <div className={`text-lg font-black ${toplamNet > 0 ? 'text-red-400' : 'text-green-400'}`}>{paraCevir(Math.abs(toplamNet))}</div>
          </div>
        </div>
      )}

      {/* Genel Toplam Panel - Mobilde Dikey Hizalama */}
      {raporSatirlari.length > 0 && (
        <div className="bg-stone-800 border border-stone-700 rounded-2xl md:rounded-3xl p-5 md:p-6 shadow-2xl print:bg-gray-100 print:border-gray-400 mt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="text-[10px] font-black text-stone-500 uppercase tracking-[0.2em]">Genel Toplam Bilgisi</div>
              <div className="text-xl md:text-2xl font-black text-white print:text-black tracking-tight uppercase">
                {AY_ADLARI[ay]} {yil}
              </div>
              <div className="flex gap-3 mt-2">
                <span className="text-[9px] font-bold bg-red-500/10 text-red-400 px-2 py-1 rounded-full border border-red-500/20">
                  {borcluSayisi} Borçlu
                </span>
                <span className="text-[9px] font-bold bg-green-500/10 text-green-400 px-2 py-1 rounded-full border border-green-500/20">
                  {odenmisSayisi} Ödenmiş
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:flex flex-wrap justify-center items-center gap-4 md:gap-8">
              <div className="text-center">
                <div className="text-stone-400 text-[10px] font-bold uppercase mb-0.5">Toplam Brüt</div>
                <div className="text-sm md:text-lg font-bold text-white print:text-black">{paraCevir(toplamBrut)}</div>
              </div>
              <div className="text-center">
                <div className="text-stone-400 text-[10px] font-bold uppercase mb-0.5">Toplam Avans</div>
                <div className="text-sm md:text-lg font-bold text-yellow-500 print:text-red-700">{paraCevir(toplamAvans)}</div>
              </div>
              <div className="text-center">
                <div className="text-stone-400 text-[10px] font-bold uppercase mb-0.5">Yapılan Ödeme</div>
                <div className="text-sm md:text-lg font-bold text-blue-400 print:text-blue-700">{paraCevir(toplamOdeme)}</div>
              </div>
              <div className="text-center col-span-2 sm:col-span-1 bg-stone-900/50 print:bg-white px-5 py-3 rounded-xl border border-stone-700/50">
                <div className="text-green-500 text-[10px] font-black uppercase mb-0.5">Net Ödenecek</div>
                <div className={`text-xl md:text-2xl font-black ${toplamNet > 0 ? 'text-red-400' : 'text-green-400'} print:text-black`}>{paraCevir(Math.abs(toplamNet))}</div>
              </div>
            </div>
          </div>
        </div>
      )} 

      {/* Rapor Başlığı (Yazdırmada Görünür) */}
      <div className="hidden print:block mb-8 border-b-2 border-stone-900 pb-4">
        <div className="text-3xl font-black tracking-tighter text-black">AGE İZOLASYON</div>
        <div className="text-sm text-gray-600 font-bold uppercase tracking-widest">Çatı Sistemleri ve İnşaat</div>
        <div className="flex justify-between items-end mt-4">
          <div className="text-lg font-bold text-black">{AY_ADLARI[ay]} {yil} Hakediş Raporu</div>
          <div className="text-xs text-gray-500 italic">Rapor Tarihi: {new Date().toLocaleDateString('tr-TR')}</div>
        </div>
      </div>

      {raporSatirlari.length === 0 && (
        <div className="text-center text-stone-700 text-sm py-16 md:py-20 bg-stone-900/50 border border-stone-800 border-dashed rounded-3xl">
          Bu ay için herhangi bir çalışma, avans veya ödeme kaydı bulunamadı.
        </div>
      )}

      {/* Usta Kartları */}
      <div className="space-y-4">
        {raporSatirlari.map(u => {
          const odendiMi = u.odemeDurumu === 'odenmis';
          const borcluMu = u.odemeDurumu === 'borclu';

          return (
            <div key={u.id} className={`bg-stone-900 border rounded-2xl overflow-hidden print:border print:border-gray-300 print:bg-white print:shadow-none shadow-xl ${
              odendiMi ? 'border-green-500/20' : borcluMu ? 'border-red-500/20' : 'border-stone-800'
            }`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between px-5 py-4 border-b border-stone-800/50 print:bg-gray-50 gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-white text-base md:text-lg print:text-black">{u.ad}</div>
                    {odendiMi && (
                      <span className="flex items-center gap-1 text-[9px] font-black bg-green-500/10 text-green-400 px-2 py-0.5 rounded-full border border-green-500/20">
                        <CheckCircle2 size={10} /> ÖDENDİ
                      </span>
                    )}
                    {borcluMu && (
                      <span className="flex items-center gap-1 text-[9px] font-black bg-red-500/10 text-red-400 px-2 py-0.5 rounded-full border border-red-500/20">
                        <AlertCircle size={10} /> BORÇLU
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mt-1 print:text-gray-500 leading-relaxed">
                    {u.lokasyonlar.length > 0 ? u.lokasyonlar.join(' • ') : 'Şantiye Kaydı Yok'}
                  </div>
                </div>

                <div className="grid grid-cols-4 sm:flex items-center gap-4 md:gap-6 border-t border-stone-800/50 pt-4 lg:pt-0 lg:border-none">
                  <div>
                    <div className="text-[10px] text-stone-500 font-bold uppercase print:text-gray-400">Mesai</div>
                    <div className="font-bold text-white text-sm md:text-base print:text-black">{u.gunler.length} G</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-stone-500 font-bold uppercase print:text-gray-400">Brüt</div>
                    <div className="font-bold text-green-400 text-sm md:text-base print:text-black">{paraCevir(u.brut)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-stone-500 font-bold uppercase print:text-gray-400">Avans</div>
                    <div className="font-bold text-yellow-500 text-sm md:text-base print:text-red-600">{paraCevir(u.avans)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-stone-500 font-bold uppercase print:text-gray-400">Ödeme</div>
                    <div className="font-bold text-blue-400 text-sm md:text-base print:text-blue-700">{paraCevir(u.odeme)}</div>
                  </div>
                  <div className="text-right col-span-4 sm:col-span-1 mt-2 sm:mt-0 pt-2 sm:pt-0 border-t sm:border-0 border-stone-800/50">
                    <div className="text-[10px] text-stone-500 font-bold uppercase print:text-gray-400">Net</div>
                    <div className={`text-base md:text-lg font-black ${u.net < 0 ? 'text-yellow-400' : u.net > 0 ? 'text-red-400' : 'text-green-400'} print:text-black`}>
                      {paraCevir(u.net)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Ödeme Kayıtları (varsa) */}
              {u.odemeKayitlari.length > 0 && (
                <div className="px-5 py-3 bg-blue-500/5 border-t border-stone-800/50 print:bg-gray-50">
                  <div className="text-[10px] text-blue-400 font-black uppercase tracking-widest mb-2 flex items-center gap-1">
                    <CreditCard size={10} /> Bu Ayın Ödemeleri
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {u.odemeKayitlari.map(odeme => {
                      const tarih = odeme.tarih?.toDate ? odeme.tarih.toDate() : new Date(odeme.tarih);
                      return (
                        <div key={odeme.id} className="flex items-center gap-1.5 bg-stone-800 print:bg-gray-100 border border-stone-700/50 print:border-gray-200 rounded-md px-2 py-1 text-[10px]">
                          <CheckCircle2 size={10} className="text-blue-400" />
                          <span className="text-white font-bold print:text-black">{paraCevir(odeme.tutar)}</span>
                          <span className="text-stone-500">{tarih.toLocaleDateString('tr-TR')}</span>
                          {odeme.aciklama && <span className="text-stone-600 italic">({odeme.aciklama})</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Gün Detayları - Mobilde Daha Akışkan */}
              <div className="px-5 py-3 bg-stone-900/30 print:bg-white">
                <div className="flex flex-wrap gap-1.5 md:gap-2">
                  {u.gunler.map(({ tarih, santiye }, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 bg-stone-800 print:bg-gray-100 border border-stone-700/50 print:border-gray-200 rounded-md px-2 py-1 text-[10px] md:text-[11px]">
                      <span className="text-stone-400 font-bold print:text-gray-500">
                        {tarih.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                      </span>
                      <span className="w-1 h-1 bg-stone-600 rounded-full"></span>
                      <span className="text-stone-200 font-medium print:text-black truncate max-w-[80px] md:max-w-none">
                        {santiye}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Yazdırma Ayarları */}
      <style>{`
        @media print {
          body { background: white !important; color: black !important; }
          .print\:hidden { display: none !important; }
          .print\:block { display: block !important; }
          nav, aside, header { display: none !important; }
          .max-w-4xl { max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
          .shadow-xl, .shadow-2xl { box-shadow: none !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  );
}